import React from 'react';

import { CustomerGlobalSearchResponseNormalized } from '../../../../../../store/customer/types';
import { FixedFeeNewContractStepOneData } from '../../../types';

interface UseStepOneCustomerSelectionProps {
  value: FixedFeeNewContractStepOneData;
  initialReplacementSelection?: {
    pnpAccountNumber: number;
  };
  customerSearchResultItems: CustomerGlobalSearchResponseNormalized[];
  updateState: (nextValue: Partial<FixedFeeNewContractStepOneData>) => void;
  closeMenu: () => void;
}

interface UseStepOneCustomerSelectionResult {
  handleSearchTermChange: (searchTerm: string) => void;
  handleSelectCustomer: (selectedCustomer: CustomerGlobalSearchResponseNormalized) => void;
  handleClearSearch: () => void;
}

export const useStepOneCustomerSelection = ({
  value,
  initialReplacementSelection,
  customerSearchResultItems,
  updateState,
  closeMenu,
}: UseStepOneCustomerSelectionProps): UseStepOneCustomerSelectionResult => {
  const hasTriedAutoSelectRef = React.useRef(false);

  const handleSearchTermChange = React.useCallback(
    (searchTerm: string) => {
      updateState({
        searchTerm,
        selectedCustomer:
          value.selectedCustomer?.customerNameNormalized === searchTerm
            ? value.selectedCustomer
            : undefined,
      });
    },
    [updateState, value.selectedCustomer]
  );

  const handleSelectCustomer = React.useCallback(
    (selectedCustomer: CustomerGlobalSearchResponseNormalized) => {
      updateState({
        selectedCustomer,
        searchTerm: selectedCustomer.customerNameNormalized,
      });

      closeMenu();
    },
    [closeMenu, updateState]
  );

  const handleClearSearch = React.useCallback(() => {
    updateState({
      searchTerm: '',
      selectedCustomer: undefined,
    });

    closeMenu();
  }, [closeMenu, updateState]);

  React.useEffect(() => {
    if (!initialReplacementSelection) {
      return;
    }

    updateState({
      searchTerm: initialReplacementSelection.pnpAccountNumber.toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReplacementSelection]);

  React.useEffect(() => {
    if (hasTriedAutoSelectRef.current || !initialReplacementSelection || value.selectedCustomer) {
      return;
    }

    if (!customerSearchResultItems.length) {
      return;
    }

    const normalizedAccount = initialReplacementSelection.pnpAccountNumber;
    const customerByAccount = customerSearchResultItems.find(
      (customer) => customer.pnpAccountNumber === normalizedAccount
    );

    if (customerByAccount) {
      handleSelectCustomer(customerByAccount);
    }

    hasTriedAutoSelectRef.current = true;
  }, [
    customerSearchResultItems,
    handleSelectCustomer,
    initialReplacementSelection,
    value.selectedCustomer,
  ]);

  return {
    handleSearchTermChange,
    handleSelectCustomer,
    handleClearSearch,
  };
};
